<script setup>
import FormError from "@/Components/UI/forms/FormError.vue";
import BaseInput from "@/Components/UI/inputs/BaseInput.vue";
import cn from "@/helpers/cn.js";
import FloatLabel from "primevue/floatlabel";
import { computed, ref, useAttrs, useId } from "vue";

const props = defineProps({
    label: {
        type: String,
        required: true,
    },
    modelValue: {
        required: true,
    },
    // Error MESSAGE (rendered via FormError). Separate from the `invalid` validity flag.
    error: String,
    // Canonical validity FLAG. When omitted, falls back to message presence so
    // existing `error`-only callers keep their red border.
    invalid: {
        type: Boolean,
        default: undefined,
        validator: (v) => typeof v === "boolean",
    },
    inputClass: String,
    disabled: Boolean,
    // "md" = padding-only default; "lg" matches button height; "sm" is a compact variant.
    size: {
        type: String,
        default: "md",
        validator: (value) => ["sm", "md", "lg"].includes(value),
    },
});

const isInvalid = computed(() => (props.invalid !== undefined ? props.invalid : Boolean(props.error)));

defineOptions({
    inheritAttrs: false,
});

defineEmits(["update:modelValue", "focus"]);

const input = ref(null);

defineExpose({
    focus() {
        input.value.focus();
    },
    input,
});

const attrs = useAttrs();
const generatedId = useId();
// Reuse a caller-supplied id (some forms set one for their own label/anchor);
// otherwise generate one so the FloatLabel <label for> binds to the input.
const inputId = computed(() => attrs.id ?? generatedId);
</script>

<template>
    <div class="text-base" :class="$attrs?.class">
        <FloatLabel variant="on" class="w-full">
            <BaseInput
                ref="input"
                :id="inputId"
                :model-value="modelValue"
                :disabled="disabled"
                :size="size"
                :invalid="isInvalid"
                :class="cn('w-full', inputClass)"
                v-bind="$attrs"
                @focus="$emit('focus')"
                @update:model-value="(val) => $emit('update:modelValue', val)" />
            <label :for="inputId">{{ label }}</label>
        </FloatLabel>
        <FormError :error="error" />
    </div>
</template>
